import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '../components/ui/SectionHeading';
import SafeIcon from '../common/SafeIcon';
import { personalInfo } from '../data/content';
import { useFormSubmit } from '../hooks/useFormSubmit';

const Contact = () => {
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const { status, error, submitForm, resetFormStatus } = useFormSubmit();

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileName(e.target.files[0].name);
    }
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const myForm = e.target;
    const formData = new FormData(myForm);
    const payload = Object.fromEntries(formData.entries());

    if (payload._honey) {
      // Spam honeypot triggered, silently exit and act like success
      myForm.reset();
      resetFormStatus('success');
      return;
    }
    delete payload._honey;

    const file = formData.get('brief');
    if (file && file.size > 0) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert("Please ensure the file is under 5MB.");
        return;
      }
      try {
        const base64File = await toBase64(file);
        payload.brief = base64File;
        payload.briefFileName = file.name;
        payload.briefMimeType = file.type;
      } catch (err) {
        console.error('File conversion failed', err);
      }
    } else {
      delete payload.brief;
    }

    payload.listName = 'Contact Submission';

    const success = await submitForm(payload);
    if (success) {
      myForm.reset();
      setFileName('');
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300 relative overflow-hidden">
      {/* Dark Grid Background */}
      <div 
        className="absolute top-0 left-0 w-full h-[600px] opacity-[0.15] dark:opacity-[0.25] pointer-events-none" 
        style={{ 
          backgroundImage: 'linear-gradient(to right, #40E0D0 1px, transparent 1px), linear-gradient(to bottom, #40E0D0 1px, transparent 1px)', 
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(circle at center, transparent 0%, black 85%)',
          WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 85%)'
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <SectionHeading title="Initiate a Conversation" subtitle="Executive Inquiry" />
            <p className="text-lg text-slate-600 dark:text-slate-400 font-medium mb-12">
              For inquiries regarding fractional CMO engagements, brand strategy consulting, or keynote speaking opportunities.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-6 p-6 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm hover:border-brand-cyan transition-colors group">
                <div className="shrink-0 w-12 h-12 bg-brand-dark dark:bg-brand-darker text-white rounded-full flex items-center justify-center group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                  <SafeIcon name="Mail" className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Direct Communication</h4>
                  <a href={`mailto:${personalInfo.email}`} className="text-brand-dark dark:text-white font-bold hover:text-brand-red dark:hover:text-brand-cyan transition-colors">
                    {personalInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-6 p-6 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm hover:border-brand-cyan transition-colors group">
                <div className="shrink-0 w-12 h-12 bg-brand-dark dark:bg-brand-darker text-white rounded-full flex items-center justify-center group-hover:bg-brand-cyan group-hover:text-brand-dark transition-colors">
                  <SafeIcon name="Linkedin" className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">LinkedIn Network</h4>
                  <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="text-brand-dark dark:text-white font-bold hover:text-brand-red dark:hover:text-brand-cyan transition-colors">
                    Professional Profile
                  </a>
                </div>
              </div>
            </div>

            {/* Post-Engagement Note */}
            <div className="mt-16 p-8 border-l-4 border-brand-red dark:border-brand-yellow bg-brand-dark dark:bg-brand-darker text-white rounded-r-xl relative overflow-hidden">
              <div className="absolute right-0 top-0 opacity-10">
                <SafeIcon name="Shield" className="w-32 h-32" />
              </div>
              <h4 className="text-sm font-bold uppercase tracking-widest text-brand-yellow mb-3 relative z-10">The Client Experience</h4>
              <p className="text-slate-300 text-sm font-medium leading-relaxed relative z-10">
                "My engagement doesn't end at delivery. Every project includes a 90-day strategic follow-up cycle to ensure operational alignment and measurable impact on your bottom line."
              </p>
            </div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-900 p-8 md:p-12 border border-slate-200 dark:border-slate-800 shadow-xl rounded-2xl"
          >
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-brand-cyan/20 text-brand-cyan rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon name="Check" className="w-10 h-10" />
                </div>
                <h3 className="text-3xl font-heading font-bold text-brand-dark dark:text-white mb-4">Inquiry Received</h3>
                <p className="text-slate-600 dark:text-slate-400 font-medium">I will review your brief and respond personally within 24 hours.</p>
                <button onClick={resetFormStatus} className="mt-8 px-6 py-2 bg-slate-100 dark:bg-slate-800 text-xs uppercase font-bold tracking-widest rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">Send Another Brief</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {status === 'error' && (
                  <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm font-medium">
                    {error || 'Failed to submit. Please try again.'}
                  </div>
                )}

                {/* Honeypot field to filter out spam */}
                <input type="text" name="_honey" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-2">Full Name</label>
                    <input name="fullName" required className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 focus:border-brand-cyan outline-none transition-colors rounded-lg font-medium dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-2">Executive Email</label>
                    <input name="email" required type="email" className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 focus:border-brand-cyan outline-none transition-colors rounded-lg font-medium dark:text-white" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-2">Strategic Interest</label>
                  <select name="interest" className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 focus:border-brand-cyan outline-none text-slate-700 dark:text-slate-300 rounded-lg font-medium">
                    <option>Fractional CMO Engagement</option>
                    <option>Brand Audit & Positioning</option>
                    <option>Marketing Operations Overhaul</option>
                    <option>AI Orchestration Strategy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-2">Attach Project Brief (Optional)</label>
                  <div 
                    onClick={() => fileInputRef.current.click()}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-dashed border-slate-300 dark:border-slate-700 px-4 py-10 text-center cursor-pointer hover:border-brand-cyan hover:bg-brand-cyan/5 transition-colors rounded-lg"
                  >
                    <SafeIcon name="UploadCloud" className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{fileName || "Click to upload executive summary (PDF, DOCX)"}</p>
                    <input type="file" name="brief" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className="w-full py-4 bg-brand-red text-white text-sm uppercase tracking-widest font-bold hover:bg-brand-yellow hover:text-brand-dark transition-all rounded-lg disabled:opacity-50 shadow-lg shadow-brand-red/20"
                >
                  {status === 'loading' ? 'Transmitting...' : 'Submit Strategic Inquiry'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;